import { useState, useEffect, useCallback } from 'react';
import { fetchStatistics, fetchSupercycleStats, resetSupercycleStats, resetLastTrainStats } from '../../services/api';
import React from 'react';
import theme from '../../theme';

// Supercycle tally: one row per ×4 pair that has seen a candidate. Averages are over finished chains; the live one is shown apart.
// Real = a supercycle that held 7+ junior waves (the candidate's own wave included); Cand = every chain a candidate opened.
const SupercycleStatsTab = ({ broker, account, symbol }) => {
  const [rows, setRows] = useState(null);
  const [lastTrain, setLastTrain] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await fetchSupercycleStats({ broker, symbol, account });
      setRows(data?.rows || []);
      setLastTrain(data?.last_train || []);
    } catch (error) {
      console.error(error);
      setRows([]);
      setLastTrain([]);
    }
  }, [broker, account, symbol]);

  useEffect(() => { load(); }, [load]);

  const handleReset = async (row) => {
    await resetSupercycleStats({
      broker, symbol, account,
      junior_timeframe: row.junior_timeframe,
      senior_timeframe: row.senior_timeframe,
    });
    load();
  };

  const handleLastTrainReset = async (row) => {
    await resetLastTrainStats({
      broker, symbol, account,
      junior_timeframe: row.junior_timeframe,
      senior_timeframe: row.senior_timeframe,
    });
    load();
  };

  if (rows === null) {
    return (
      <div className="flex justify-center py-6">
        <div className={`w-7 h-7 border-2 ${theme.spinner} rounded-full animate-spin`} />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <>
        <div className="text-center py-8">
          <h3 className={`text-sm font-semibold ${theme.textPrimary}`}>No Candidates Yet</h3>
          <p className={`mt-1 text-xs ${theme.textMuted}`}>Rows appear once a timeframe pair puts up a supercycle candidate.</p>
        </div>
        <LastTrainStatsTable rows={lastTrain} onReset={handleLastTrainReset} />
      </>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className={`${theme.textMuted} uppercase tracking-wider text-[10px]`}>
            <th className="text-left py-1.5 pr-2">Pair</th>
            <th className="text-right py-1.5 px-2">Cnt</th>
            <th className="text-right py-1.5 px-2">Avg waves</th>
            <th className="text-right py-1.5 px-2">Avg cycles</th>
            <th className="text-right py-1.5 px-2">Real</th>
            <th className="text-right py-1.5 px-2">Avg real w</th>
            <th className="text-right py-1.5 px-2">Cand</th>
            <th className="text-right py-1.5 px-2">Avg cand w</th>
            <th className="py-1.5" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.junior_timeframe}-${row.senior_timeframe}`} className="border-t border-olive-100">
              <td className={`py-1.5 pr-2 font-semibold ${theme.textPrimary}`}>
                {row.junior_timeframe} → {row.senior_timeframe}
                {(row.open_waves > 0 || row.open_cycles > 0 || row.live_chain_waves > 0) && (
                  <span className={`ml-1.5 font-normal ${theme.textMuted}`}>
                    (live: {row.open_waves}w / {row.open_cycles}c
                    {row.live_chain_waves > 0 && ` · chain ${row.live_chain_waves}w`})
                  </span>
                )}
              </td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>{row.supercycles}</td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>{row.avg_waves ?? '—'}</td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>{row.avg_cycles ?? '—'}</td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>{row.real_supercycles ?? 0}</td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>{row.avg_real_waves ?? '—'}</td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>{row.candidates ?? 0}</td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>{row.avg_candidate_waves ?? '—'}</td>
              <td className="text-right py-1.5">
                <button
                  onClick={() => handleReset(row)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-semibold ${theme.pillInactive}`}
                >
                  Reset
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <LastTrainStatsTable rows={lastTrain} onReset={handleLastTrainReset} />
    </div>
  );
};

// Last Train tally: per timeframe pair, how many alerts fired and how they resolved.
const LastTrainStatsTable = ({ rows, onReset }) => {
  if (!rows?.length) return null;
  return (
    <div className="mt-4 overflow-x-auto">
      <p className={`text-[11px] font-semibold ${theme.textMuted} uppercase tracking-wider mb-1`}>Last Train</p>
      <table className="w-full text-xs">
        <thead>
          <tr className={`${theme.textMuted} uppercase tracking-wider text-[10px]`}>
            <th className="text-left py-1.5 pr-2">Pair</th>
            <th className="text-right py-1.5 px-2">Fired</th>
            <th className="text-right py-1.5 px-2">Ok</th>
            <th className="text-right py-1.5 px-2">Fail</th>
            <th className="text-right py-1.5 px-2">Rate</th>
            <th className="py-1.5" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.junior_timeframe}-${row.senior_timeframe}`} className="border-t border-olive-100">
              <td className={`py-1.5 pr-2 font-semibold ${theme.textPrimary}`}>
                {row.junior_timeframe} → {row.senior_timeframe}
              </td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>{row.fired}</td>
              <td className="text-right px-2 text-emerald-600">{row.succeeded}</td>
              <td className="text-right px-2 text-red-500">{row.failed}</td>
              <td className={`text-right px-2 ${theme.textPrimary}`}>
                {row.success_rate === null || row.success_rate === undefined ? '—' : `${row.success_rate}%`}
              </td>
              <td className="text-right py-1.5">
                <button
                  onClick={() => onReset(row)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-semibold ${theme.pillInactive}`}
                >
                  Reset
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const KPIStatistic = ({ broker, account, symbol, sectype }) => {
  const [activeTab, setActiveTab] = useState('pnl');
  const [selectedPeriod, setSelectedPeriod] = useState('DAY');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [animationsReady, setAnimationsReady] = useState(false);

  const calculateDateRange = useCallback((period) => {
    const end = new Date();
    const start = new Date();
    switch (period) {
      case 'DAY': start.setDate(start.getDate() - 1); break;
      case 'WEEK': start.setDate(start.getDate() - 7); break;
      case 'MONTH': start.setMonth(start.getMonth() - 1); break;
      case 'YTD': start.setMonth(0); start.setDate(1); break;
      default: start.setMonth(start.getMonth() - 1);
    }
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    return { startDate: formatDate(start), endDate: formatDate(end) };
  }, []);

  const formatDateForApi = (dateStr, isEnd = false) => {
    return `${dateStr} ${isEnd ? '23:59:59' : '00:00:00'}`;
  };

  const fetchData = useCallback(async (start, end) => {
    setLoading(true);
    setAnimationsReady(false);
    try {
      const stats = await fetchStatistics(
        broker, account, symbol, sectype,
        formatDateForApi(start),
        formatDateForApi(end, true)
      );
      const hasValidAvgTime = stats && stats.avg_trade_time !== 0 && stats.avg_trade_time !== '0';
      setData(hasValidAvgTime ? stats : null);
    } catch (error) {
      console.error(error);
      setData(null);
    } finally {
      setLoading(false);
      setTimeout(() => setAnimationsReady(true), 150);
    }
  }, [broker, account, symbol, sectype]);

  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    const newDateRange = calculateDateRange(period);
    setDateRange(newDateRange);
    fetchData(newDateRange.startDate, newDateRange.endDate);
  };

  useEffect(() => {
    const initialDateRange = calculateDateRange(selectedPeriod);
    setDateRange(initialDateRange);
    fetchData(initialDateRange.startDate, initialDateRange.endDate);
  }, [broker, account, symbol, sectype]);

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const hasDataToDisplay = !loading && data;
  const showNoDataMessage = !loading && !data;

  return (
    <div className={`p-4 ${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} w-full mx-auto`}>
      <style>{`
        .staggered-card {
          opacity: 0; transform: translateY(12px);
          transition: opacity 350ms ease, transform 350ms cubic-bezier(0.4,0,0.2,1);
        }
        .staggered-card-visible { opacity: 1; transform: translateY(0); }
      `}</style>

      <div className={`flex justify-center gap-1.5 ${theme.pillContainer} mb-3`}>
        {[['pnl', 'Trading'], ['cycles', 'Cycles']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 ${activeTab === key ? theme.pillActive : theme.pillInactive}`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'cycles' ? (
        <SupercycleStatsTab broker={broker} account={account} symbol={symbol} />
      ) : (
      <>
      {/* Header */}
      <div className="mb-4 pb-3 border-b border-olive-200">
        <p className={`text-sm font-bold ${theme.textPrimary} tracking-wide text-center mb-3`}>
          Trading Statistics
        </p>
        <div className={`flex justify-center gap-1.5 ${theme.pillContainer} mb-2`}>
          {['DAY', 'WEEK', 'MONTH', 'YTD'].map((period) => (
            <button
              key={period}
              onClick={() => handlePeriodSelect(period)}
              className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 ${selectedPeriod === period ? theme.pillActive : theme.pillInactive
                }`}
            >
              {period}
            </button>
          ))}
        </div>
        {dateRange.startDate && dateRange.endDate && (
          <div className={`text-[11px] ${theme.textMuted} text-center`}>
            {formatDisplayDate(dateRange.startDate)} — {formatDisplayDate(dateRange.endDate)}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="relative" style={{ minHeight: loading ? '80px' : 'auto' }}>
        {loading && (
          <div className="absolute inset-0 flex justify-center items-center">
            <div className={`w-7 h-7 border-2 ${theme.spinner} rounded-full animate-spin`} />
          </div>
        )}

        <div className={`transition-opacity duration-300 ${!loading && animationsReady ? 'opacity-100' : 'opacity-0'}`}>
          {hasDataToDisplay ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {data.avg_trade_time && data.avg_trade_time !== 0 && data.avg_trade_time !== '0' && (
                <div
                  className={`md:col-span-2 p-3 ${theme.sectionBg} rounded-xl border border-olive-100 staggered-card ${animationsReady ? 'staggered-card-visible' : ''}`}
                  style={{ transitionDelay: '50ms' }}
                >
                  <p className={`text-[11px] font-medium ${theme.textMuted} uppercase tracking-wider`}>Avg Time In Trade</p>
                  <p className={`text-lg font-bold ${theme.textPrimary} mt-0.5`}>{data.avg_trade_time}</p>
                </div>
              )}
              {data.statistics?.map((stat, index) => (
                <React.Fragment key={index}>
                  <div
                    className={`p-3 ${theme.sectionBg} rounded-xl border border-olive-100 staggered-card ${animationsReady ? 'staggered-card-visible' : ''}`}
                    style={{ transitionDelay: '150ms' }}
                  >
                    <p className={`text-[11px] font-medium ${theme.textMuted} uppercase tracking-wider`}>P/L Ratio</p>
                    <p className={`text-lg font-bold mt-0.5 ${stat.profit_loss_ratio >= 1 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {Number(stat.profit_loss_ratio).toFixed(2)}
                    </p>
                  </div>
                  <div
                    className={`p-3 ${theme.sectionBg} rounded-xl border border-olive-100 staggered-card ${animationsReady ? 'staggered-card-visible' : ''}`}
                    style={{ transitionDelay: '250ms' }}
                  >
                    <p className={`text-[11px] font-medium ${theme.textMuted} uppercase tracking-wider`}>Total PnL</p>
                    <p className={`text-lg font-bold mt-0.5 ${stat.totalPnl >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {Number(stat.totalPnl).toFixed(2)}
                    </p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          ) : showNoDataMessage ? (
            <div className="text-center py-8">
              <svg className={`mx-auto h-10 w-10 ${theme.textMuted}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <h3 className={`mt-2 text-sm font-semibold ${theme.textPrimary}`}>No Data Found</h3>
              <p className={`mt-1 text-xs ${theme.textMuted}`}>No statistics available for the selected period.</p>
            </div>
          ) : null}
        </div>
      </div>
      </>
      )}
    </div>
  );
}

export default KPIStatistic;